import { useCallback, useEffect, useState } from "react";
import { useTheme } from "../components/ThemeProvider";
import api from "../axiosConfig";

function ScoreBar({ value, color }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div style={{ height: 6, borderRadius: 3, background: "rgba(128,128,128,0.2)", overflow: "hidden" }}>
      <div style={{ width: `${v}%`, height: "100%", background: color, borderRadius: 3 }} />
    </div>
  );
}

export default function AgentesRendimiento() {
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const [rows, setRows] = useState([]);
  const [flagActivo, setFlagActivo] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [recalcando, setRecalcando] = useState(false);
  const [error, setError] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const { data } = await api.get("/agentes/performance-scores", { timeout: 30000 });
      setRows(data?.data || []);
      setFlagActivo(!!data?.flag_activo);
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Error al cargar ranking");
    } finally {
      setCargando(false);
    }
  }, []);

  const recalcular = useCallback(async () => {
    setRecalcando(true);
    setError("");
    try {
      const { data } = await api.post("/agentes/performance-scores/recalcular", {}, { timeout: 120000 });
      setRows(
        (data?.data || []).map((r) => ({
          ...r,
          agente_nombre: r.nombre,
        }))
      );
      setFlagActivo(!!data?.flag_activo);
      await cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Error al recalcular");
    } finally {
      setRecalcando(false);
    }
  }, [cargar]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  return (
    <div style={{ padding: "16px 20px", maxWidth: 980, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: t.textPrimary }}>
            Rendimiento agentes (Ana Fríos)
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: t.textMuted }}>
            Atención 30d + Cualificación 90d → score_asignacion. Sombra siempre; sorteo ponderado solo con flag.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: "4px 8px",
              borderRadius: 6,
              border: `1px solid ${flagActivo ? "rgba(22,163,74,0.4)" : t.border}`,
              color: flagActivo ? "#16a34a" : t.textMuted,
              background: flagActivo ? "rgba(22,163,74,0.1)" : "transparent",
            }}
          >
            ANA_FRIOS_ASSIGN_BY_SCORE: {flagActivo ? "ON" : "OFF (sombra)"}
          </span>
          <button
            type="button"
            onClick={cargar}
            disabled={cargando || recalcando}
            style={{
              padding: "6px 10px",
              fontSize: 12,
              borderRadius: 6,
              border: `1px solid ${t.border}`,
              background: "transparent",
              color: t.textPrimary,
              cursor: "pointer",
            }}
          >
            {cargando ? "Cargando…" : "Actualizar"}
          </button>
          <button
            type="button"
            onClick={recalcular}
            disabled={cargando || recalcando}
            style={{
              padding: "6px 10px",
              fontSize: 12,
              fontWeight: 700,
              borderRadius: 6,
              border: "none",
              background: t.accentBlue || "#2563eb",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            {recalcando ? "Recalculando…" : "Recalcular ahora"}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 10, fontSize: 12, color: "#dc2626" }}>{error}</div>
      )}

      <div
        style={{
          border: `1px solid ${t.border}`,
          borderRadius: 10,
          overflow: "hidden",
          background: isDark ? "rgba(255,255,255,0.03)" : "#fff",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "40px 1.4fr 0.7fr 0.7fr 0.7fr 0.5fr 0.5fr",
            gap: 8,
            padding: "8px 12px",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: t.textMuted,
            borderBottom: `1px solid ${t.border}`,
            background: isDark ? "rgba(0,0,0,0.2)" : "rgba(15,23,42,0.03)",
          }}
        >
          <span>#</span>
          <span>AGENTE</span>
          <span>ASIGNACIÓN</span>
          <span>ATENCIÓN</span>
          <span>CUALIF.</span>
          <span>TAREAS</span>
          <span>PLANT.</span>
        </div>

        {cargando && rows.length === 0 ? (
          <div style={{ padding: 16, fontSize: 12, color: t.textMuted }}>Cargando ranking…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 16, fontSize: 12, color: t.textMuted }}>
            Sin scores aún. Pulsa «Recalcular ahora» o espera al cron diario.
          </div>
        ) : (
          rows.map((r, i) => {
            const det = typeof r.detalle === "string" ? JSON.parse(r.detalle || "{}") : r.detalle || {};
            const attBaja = det?.atencion?.muestra_baja;
            const qualBaja = det?.cualificacion?.muestra_baja;
            return (
              <div
                key={r.agente_id || r.id || i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "40px 1.4fr 0.7fr 0.7fr 0.7fr 0.5fr 0.5fr",
                  gap: 8,
                  padding: "10px 12px",
                  borderTop: i === 0 ? "none" : `1px solid ${t.border}`,
                  alignItems: "center",
                  fontSize: 12,
                  color: t.textPrimary,
                }}
              >
                <span style={{ fontWeight: 700, color: t.textMuted }}>{i + 1}</span>
                <div>
                  <div style={{ fontWeight: 700 }}>{r.agente_nombre || r.nombre}</div>
                  <div style={{ fontSize: 10, color: t.textMuted, marginTop: 2 }}>
                    {attBaja || qualBaja ? "muestra baja → neutro 50 en bloques afectados" : "muestra OK"}
                    {r.calculado_at
                      ? ` · ${new Date(r.calculado_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}`
                      : ""}
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{Number(r.score_asignacion)}</div>
                  <ScoreBar value={r.score_asignacion} color="#2563eb" />
                </div>
                <div>
                  <div style={{ marginBottom: 4 }}>{Number(r.score_atencion)}</div>
                  <ScoreBar value={r.score_atencion} color="#16a34a" />
                </div>
                <div>
                  <div style={{ marginBottom: 4 }}>{Number(r.score_cualificacion)}</div>
                  <ScoreBar value={r.score_cualificacion} color="#7c3aed" />
                </div>
                <span style={{ color: t.textMuted }}>{r.muestra_tareas ?? "—"}</span>
                <span style={{ color: t.textMuted }}>{r.muestra_plantillas ?? "—"}</span>
              </div>
            );
          })
        )}
      </div>

      <p style={{ marginTop: 12, fontSize: 11, color: t.textMuted, lineHeight: 1.45 }}>
        Pesos: asignación = 0,55·atención + 0,45·cualificación. Atención: a tiempo 35 %, no vencidas 20 %,
        rapidez 20 %, interacción 15 %, ritmo 10 %. Cualificación: reply 30 %, reply útil 30 %, conversión 20 %,
        feedback visita (Mala/Buena/Excelente) 20 %. Muestra baja → neutro 50 en ese bloque.
      </p>
    </div>
  );
}
